import React, { useState, useEffect, ChangeEvent } from "react";
import {
  ClusterWrap,
  FormContainer,
  FormContainerItem,
  PrimaryButton,
  SecondaryButton,
  Select,
  TextArea,
  TextField,
} from "@graffer-inc/console-design-kit";
import {
  StringGenerator,
  ValidationErrorVariables,
} from "@graffer-inc/form-schema";
import {
  CHECKBOXES,
  ElemType,
  RADIO_BUTTONS,
  SELECT,
  TargetField,
} from "../../../../../application/domain/procedure-assignment-setting-ruleset/procedure-assignment-setting-ruleset";
import { BaseModal } from "../../../../atoms/base-modal";

interface TargetFieldModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (targetField: TargetField) => void;
  initialData?: TargetField;
  existingIds: string[];
}

const elemTypeOptions = [
  { value: RADIO_BUTTONS, name: "ラジオボタン" },
  { value: SELECT, name: "セレクトボックス" },
  { value: CHECKBOXES, name: "チェックボックス" },
];

const expectedValuesPlaceholder = `事業部
開発部
経理部
=> 選択肢[事業部, 開発部, 経理部]`;

const maxValues = {
  select: ValidationErrorVariables.select.maxEnumLength,
  radio_buttons: ValidationErrorVariables.radio.maxEnumLength,
  checkboxes: ValidationErrorVariables.checkboxes.maxEnumLength,
};

const validateExpectedValues = (
  expectedValuestext: string,
  expectedValues: string[],
  elemType: ElemType,
): string[] => {
  const errors: string[] = [];

  // 文字数チェック
  if (
    expectedValuestext.length > ValidationErrorVariables.text_area.maxLength
  ) {
    errors.push(
      `${ValidationErrorVariables.text_area.maxLength}文字以内で入力してください。`,
    );
  }

  // 選択肢の文字数チェック
  if (
    expectedValues.some(
      (value) =>
        value.length > ValidationErrorVariables.selectables.enumTextMaxLength,
    )
  ) {
    errors.push(
      `選択肢に設定できる文字数は${ValidationErrorVariables.selectables.enumTextMaxLength}文字以内です。`,
    );
  }

  // 選択肢の数チェック
  if (expectedValues.length === 0) {
    errors.push("選択肢を1つ以上入力してください。");
  } else if (expectedValues.length > maxValues[elemType]) {
    errors.push(
      `${elemTypeOptions.find((option) => option.value === elemType)?.name} の選択肢の数は最大${maxValues[elemType]}個までです。`,
    );
  }

  return errors;
};

const validateDescription = (description: string): string[] => {
  const errors: string[] = [];
  const trimmedDescription = description.trim();
  if (!trimmedDescription) {
    errors.push("説明を入力してください。");
  } else if (
    trimmedDescription.length > ValidationErrorVariables.description.maxLength
  ) {
    errors.push(
      `${ValidationErrorVariables.description.maxLength}文字以内で入力してください。`,
    );
  }
  return errors;
};

export const TargetFieldModal: React.FC<TargetFieldModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialData,
  existingIds,
}) => {
  const [description, setDescription] = useState<string>("");
  const [elemType, setElemType] = useState<ElemType>("select");
  const [expectedValues, setExpectedValues] = useState<string[]>([]);
  const [expectedValuesText, setExpectedValuesText] = useState<string>("");
  const [elemId, setElemId] = useState<string>("");
  const [descriptionErrorMessages, setDescriptionErrorMessages] = useState<
    string[]
  >([]);
  const [expectedValuesErrorMessages, setExpectedValuesErrorMessages] =
    useState<string[]>([]);

  useEffect(() => {
    if (initialData) {
      setDescription(initialData.description);
      setElemType(initialData.elemType);
      setExpectedValues(initialData.expectedValues);
      setExpectedValuesText(initialData.expectedValues.join("\n"));
      setElemId(initialData.elemId);
    } else {
      setElemId(StringGenerator.generateUnique(existingIds));
      setDescription("");
      setElemType("select");
      setExpectedValues([]);
      setExpectedValuesText("");
    }

    // モーダルが開閉されるたびにエラーメッセージをリセット
    setDescriptionErrorMessages([]);
    setExpectedValuesErrorMessages([]);
  }, [initialData, isOpen, existingIds]);

  const handleSave = () => {
    if (!validateTargetField()) {
      return;
    }

    const targetField: TargetField = {
      elemId,
      description,
      elemType,
      expectedValues,
    };

    onSave(targetField);
  };

  const handleExpectedValuesChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setExpectedValuesText(e.target.value);
    const values = e.target.value
      .split("\n")
      .map((value) => value.trim())
      .filter((value) => value !== "");
    setExpectedValues(values);
    setExpectedValuesErrorMessages(
      validateExpectedValues(e.target.value, values, elemType),
    );
  };

  const validateTargetField = (): boolean => {
    const descriptionErrors = validateDescription(description);
    const expectedValuesErrors = validateExpectedValues(
      expectedValuesText,
      expectedValues,
      elemType,
    );

    setDescriptionErrorMessages(descriptionErrors);
    setExpectedValuesErrorMessages(expectedValuesErrors);

    return descriptionErrors.length === 0 && expectedValuesErrors.length === 0;
  };

  const modalTitle = initialData ? "選択項目の編集" : "選択項目の追加";

  return (
    <BaseModal isActive={isOpen} title={modalTitle} onClose={onClose}>
      <section className="modal-card-body">
        <FormContainer>
          <FormContainerItem>
            {/* graffer-schema の ElementTag は export されていないので同じものを実装した https://github.com/graffer-inc/graffer-schemas/blob/db161cacd5538bc94c5f82abb521fdbabd7b16db/libs/packages/form-builder/lib/elements/editor-pane/element-editor-pane/element-tags.tsx#L15-L20  */}
            <div className="control">
              <div className="tags has-addons">
                <span className="tag is-light">項目ID</span>
                <span className="tag is-link is-light">{elemId}</span>
              </div>
            </div>
          </FormContainerItem>

          <FormContainerItem>
            <TextField
              description="選択項目の説明を入力してください"
              label="項目の説明"
              value={description}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                setDescription(e.target.value);
                setDescriptionErrorMessages(
                  validateDescription(e.target.value),
                );
              }}
              placeholder="例: 部署選択項目"
              required
              errorMessages={descriptionErrorMessages}
            />
          </FormContainerItem>

          <FormContainerItem>
            <Select
              description="選択項目のタイプを選択してください"
              label="項目タイプ"
              value={elemType}
              onChange={(value) => {
                setElemType(value as ElemType);
                setExpectedValuesErrorMessages(
                  validateExpectedValues(
                    expectedValuesText,
                    expectedValues,
                    value as ElemType,
                  ),
                );
              }}
              options={elemTypeOptions}
              required
            />
          </FormContainerItem>

          <FormContainerItem>
            <TextArea
              description="選択肢を改行で区切って入力してください"
              label="期待される選択肢"
              value={expectedValuesText}
              onChange={handleExpectedValuesChange}
              placeholder={expectedValuesPlaceholder}
              rows={5}
              required
              errorMessages={expectedValuesErrorMessages}
              maxLength={ValidationErrorVariables.text_area.maxLength}
            />
          </FormContainerItem>
          <FormContainerItem>
            <div className="tags">
              {expectedValues.map((v, i) => (
                <span
                  className="tag pt-2 pb-2"
                  style={{
                    whiteSpace: "normal",
                    height: "auto",
                    wordBreak: "break-word",
                  }}
                  key={`selectable-options-tag-${i}`}
                >
                  {v}
                </span>
              ))}
            </div>
          </FormContainerItem>
        </FormContainer>
      </section>
      <footer className="modal-card-foot is-justify-content-center">
        <ClusterWrap>
          <SecondaryButton text="キャンセル" onClick={onClose} />
          <PrimaryButton text="保存" onClick={handleSave} />
        </ClusterWrap>
      </footer>
    </BaseModal>
  );
};
